import React, { useState, useContext } from 'react';
import { PageHeader, SectionTitle, Card, Badge } from '../design-system';
import UserProfileContext from '../context/userprofilecontext';
import { useGetCaseCost, refreshCaseCost, useGetCaseProfitability } from '../api/casecost';
import { formatCurrency, formatInteger, formatPercentage } from '../utils/formatters';
import { periodRange } from '../utils/costPeriod';
import CostPeriodFilter from '../components/ui/CostPeriodFilter';
import CostBasisToggle from '../components/ui/CostBasisToggle';
import styles from './page-case-costs.module.css';

const dollars = (v) => `$${formatCurrency(v ?? 0)}`;
// Margin sign → token-driven color class (positive green, negative red).
const signClass = (v) => (v > 0 ? styles.marginPos : v < 0 ? styles.marginNeg : '');
// margin % arrives as a fraction; null when revenue is 0 (undefined %).
const pct = (v) => (v === null || v === undefined ? '—' : formatPercentage(v));

// ---- Cost summary ---------------------------------------------------------

function CostStat({ label, value, note }) {
  return (
    <Card padding="md">
      <div className={styles.costStatLabel}>{label}</div>
      <div className={styles.costStatValue}>{dollars(value)}</div>
      {note && <div className={styles.costStatNote}>{note}</div>}
    </Card>
  );
}

function MarginStat({ label, value, pctValue, note }) {
  return (
    <Card padding="md">
      <div className={styles.costStatLabel}>
        {label}{note && <span className={styles.estTag}> {note}</span>}
      </div>
      <div className={`${styles.costStatValue} ${signClass(value)}`}>{dollars(value)}</div>
      <div className={styles.costStatNote}>Margin {pct(pctValue)}</div>
    </Card>
  );
}

function Breakdown({ title, entries }) {
  const rows = Object.entries(entries || {}).sort((a, b) => b[1] - a[1]);
  return (
    <Card padding="md">
      <SectionTitle>{title}</SectionTitle>
      {rows.length === 0 ? (
        <p className={styles.empty}>No cost recorded for this range.</p>
      ) : (
        <ul className={styles.breakdownList}>
          {rows.map(([key, value]) => (
            <li key={key} className={styles.breakdownRow}>
              <span className={styles.breakdownLabel}>{key}</span>
              <span className={styles.breakdownValue}>{dollars(value)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---- Page -----------------------------------------------------------------

export default function CaseCostsPage() {
  const { userProfile } = useContext(UserProfileContext);
  const caseID = userProfile?.currentCase?.caseID || null;

  const [periodKey, setPeriodKey] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const { start, end } = periodRange(periodKey, customStart, customEnd);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState('');
  const [basis, setBasis] = useState('accrual');

  const { data, isLoading, error } = useGetCaseCost(caseID, start, end);
  const { data: profData } = useGetCaseProfitability(caseID, start, end);

  if (!caseID) {
    return (
      <div className={styles.page}>
        <PageHeader title="Case Costs" subtitle="Internal cost to run this case" />
        <Card padding="md">
          <p className={styles.empty}>Select a case to view its cost.</p>
        </Card>
      </div>
    );
  }

  const needsCustom = periodKey === 'custom' && !(start && end);

  const onRefresh = async () => {
    setRefreshing(true);
    setRefreshNote('');
    try {
      const res = await refreshCaseCost(caseID);
      setRefreshNote(
        res?.refreshed
          ? 'Refreshed with live current-month cost.'
          : 'Case is idle — figures are already current as of the latest snapshot.'
      );
    } catch (e) {
      setRefreshNote('Refresh failed. Try again.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className={styles.page}>
      <PageHeader
        title="Case Costs"
        subtitle="Internal cost to run this case — not customer pricing. Super-admins only."
      />

      <section className={styles.section}>
        <CostPeriodFilter
          periodKey={periodKey}
          customStart={customStart}
          customEnd={customEnd}
          onPeriodKey={setPeriodKey}
          onCustomStart={setCustomStart}
          onCustomEnd={setCustomEnd}
        />
      </section>

      {needsCustom && (
        <Card padding="md"><p className={styles.empty}>Pick a start and end date.</p></Card>
      )}
      {isLoading && !needsCustom && (
        <Card padding="md"><p className={styles.empty}>Loading cost…</p></Card>
      )}
      {error && (
        <Card padding="md"><p className={styles.empty}>Could not load cost.</p></Card>
      )}

      {data && !needsCustom && (
        <>
          <section className={styles.section}>
            <div className={styles.refreshBar}>
              <p className={styles.caption}>
                Snapshot-derived — matches the System Costs view for this case. Updated daily
                {data.snapshot_through ? ` (latest snapshot ${data.snapshot_through})` : ''}.
              </p>
              <button
                type="button"
                className={styles.refreshBtn}
                onClick={onRefresh}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing…' : 'Refresh live'}
              </button>
            </div>
            {refreshNote && <p className={styles.hint}>{refreshNote}</p>}
          </section>

          <section className={styles.section}>
            <SectionTitle>Cost summary</SectionTitle>
            <div className={styles.statGrid}>
              <CostStat label="AI / Anthropic" value={data.ai_usd} note="Chat + pipeline" />
              <CostStat label="AWS Infrastructure" value={data.aws_direct_usd} note="Tagged to this case" />
              <CostStat label="Direct total" value={data.direct_usd} />
            </div>
            <p className={styles.caption}>
              Direct cost (AI + tagged AWS) for the selected range — {formatInteger(data.tokens || 0)} AI
              tokens, {formatInteger(data.pages || 0)} OCR pages. AWS is month-to-date through ~yesterday
              (Cost Explorer lag).
            </p>
          </section>

          <section className={styles.section}>
            <SectionTitle>Breakdown</SectionTitle>
            <div className={styles.breakdownGrid}>
              <Breakdown title="AI — chat by session type" entries={data.by_session_type} />
              <Breakdown title="AI — pipeline by source" entries={data.by_source} />
              <Breakdown title="AWS by service" entries={data.by_service} />
            </div>
            <p className={styles.caption}>
              Breakdowns are captured per month from when this view shipped; earlier months show totals
              only. Use “Refresh live” to capture the current month now.
            </p>
          </section>

          {profData && (
            <section className={styles.section}>
              <div className={styles.profHeader}>
                <SectionTitle>Profitability</SectionTitle>
                <CostBasisToggle basis={basis} onBasis={setBasis} />
              </div>
              <div className={styles.statGrid}>
                <CostStat label="Revenue (billed)" value={profData[basis].revenue} note="From billing ledger" />
                <MarginStat
                  label="Direct margin"
                  value={profData[basis].margin_direct}
                  pctValue={profData[basis].margin_pct_direct}
                />
                <MarginStat
                  label="Fully-loaded margin"
                  value={profData[basis].margin_loaded}
                  pctValue={profData[basis].margin_pct_loaded}
                  note="(incl. est. overhead)"
                />
              </div>
              <p className={styles.caption}>
                Billed revenue (from the billing ledger) minus internal cost for the selected range.{' '}
                <strong>Accrual</strong> counts all months including the current open month (revenue and cost
                accrued to date); <strong>Cash</strong> counts only finalized (closed) months.{' '}
                <strong>Fully-loaded</strong> also subtracts an <strong>estimated</strong> share of shared
                overhead. Internal — not customer pricing.
              </p>
              {profData.rows?.length > 0 && (
                <Card padding="none" className={styles.monthCard}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th}>Month</th>
                        <th className={styles.th}>Status</th>
                        <th className={`${styles.th} ${styles.num}`}>Revenue</th>
                        <th className={`${styles.th} ${styles.num}`}>Direct cost</th>
                        <th className={`${styles.th} ${styles.num}`}>Loaded cost</th>
                        <th className={`${styles.th} ${styles.num}`}>Direct margin</th>
                        <th className={`${styles.th} ${styles.num}`}>Loaded margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profData.rows.map((r) => (
                        <tr key={r.period}>
                          <td className={styles.cell}>{r.period}</td>
                          <td className={styles.cell}>
                            <Badge variant={r.status === 'FINALIZED' ? 'success' : 'warning'}>
                              {r.status === 'FINALIZED' ? 'Closed' : 'Open'}
                            </Badge>
                          </td>
                          <td className={`${styles.cell} ${styles.num}`}>{dollars(r.revenue)}</td>
                          <td className={`${styles.cell} ${styles.num}`}>{dollars(r.cost_direct)}</td>
                          <td className={`${styles.cell} ${styles.num}`}>{dollars(r.cost_loaded)}</td>
                          <td className={`${styles.cell} ${styles.num} ${signClass(r.margin_direct)}`}>{dollars(r.margin_direct)}</td>
                          <td className={`${styles.cell} ${styles.num} ${signClass(r.margin_loaded)}`}>{dollars(r.margin_loaded)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
